import scrollToElement from "scroll-to-element";
import Swiper from "swiper";
import tick from "../helpers/async";
import { setCSSCustomProperty } from "../helpers/css";
import wait from '../helpers/wait';

const isPage = slug => document.body.classList.contains(slug);

export const UIController = {
  init() {
    return new Promise((resolve, reject) => {
      try {

        this.initMeasurementCheckers();
        this.recordNavBarHeight();

        if (isPage('home')) {
          this.initFooterSlider();
        }

        resolve();
      } catch (error) {
        reject(error);
      }
    })
  },

  recordWindowDimensions() {
    const { innerWidth, innerHeight } = window;
    setCSSCustomProperty('--vw', innerWidth + 'px');
    setCSSCustomProperty('--vh', innerHeight + 'px');
  },

  recordWpAdminBarHeight() {
    const el = document.getElementById('wpadminbar');
    const height = el ? el.clientHeight : 0;
    setCSSCustomProperty('--wp-admin-bar-height', height + 'px');
    return height;
  },

  recordNavBarHeight() {
    const el = document.querySelector('.site-header');
    const height = el ? el.clientHeight : 0;
    setCSSCustomProperty('--nav-height', height + 'px');
    return height;
  },

  recordNavSearchBarHeight() {
    const el = document.querySelector('.search-form-wrapper');
    const height = el ? el.clientHeight : 0;
    setCSSCustomProperty('--nav-search-form-height', height + 'px');
    return height;
  },

  recordContentTabsHeight() {
    const el = document.querySelector('.section-content-tabs');
    const height = el ? el.clientHeight : 0;
    setCSSCustomProperty('--content-tabs-height', height + 'px');
    return height;
  },

  recordHeaderHeight() {
    const el = document.querySelector('.woocommerce-products__header') || document.querySelector('.standard-page__header') || document.querySelector('.gallery__header');
    const height = el ? el.clientHeight : 0;
    setCSSCustomProperty('--page-header-height', height + 'px');
    return height;
  },

  initMeasurementCheckers() {

    const handleWindowLoad = () => {
      this.recordWindowDimensions();
      this.recordWpAdminBarHeight();
      this.recordNavBarHeight();
      this.recordNavSearchBarHeight();
      this.recordHeaderHeight();
    }

    const handleWindowResize = () => {
      this.recordWindowDimensions();
      this.recordWpAdminBarHeight();
      this.recordNavBarHeight();
      this.recordNavSearchBarHeight();
      this.recordHeaderHeight();
    }

    window.addEventListener('load', handleWindowLoad);
    window.addEventListener('resize', handleWindowResize);
  },

  initAnchorLinksScroller() {
    const els = document.querySelectorAll('.section-nav a[href*="#"], .view-main a[href*="#"]');
    const handler = e => {
      try {
        const { target } = e;
        const href = target.getAttribute('href');
        const match = href.match(/([^#]*)#(.*)/);
        if (!match) return;
        const pathname = match[1];

        if (!!pathname && pathname !== window.location.pathname) return;

        // do not do anything if the thing links to a different page
        const id = match[2];
        if (!id) return;
        // only prevent default if an id is found
        e.preventDefault();
        this.scrollToId(id); 
      } catch(e) {
        console.error(e);
        return;
      }
    };
    Array.from(els).forEach(el => {
      el.addEventListener('click', handler);
    })
  },
  async checkStoredHash() {
    if (window.ID_HASH) {
      await wait(500);
      this.scrollToId(window.ID_HASH);
      await wait(618);
      window.location.hash = window.ID_HASH;
    }
  },
  scrollToId(id) {
    if (!id) this.scrollToTop();
    else {
      const offset = -1 * (
        this.recordWpAdminBarHeight() + 
        this.recordNavBarHeight() +
        this.recordContentTabsHeight()
      );
      scrollToElement('#' + id, { offset, ease: 'inOutCube', duration: 618 });
    }
  },
  scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  },
  getSiteTitle() {
    return document.body.getAttribute('data-site-title');
  },
  getPageTitle() {
    const titleEl = document.querySelector('.page-header__title') || document.querySelector('h1') || document.querySelector('title');
    const title = titleEl && titleEl.innerText;
    return title || '';
  },
  initContentTabs() {
    const el = document.querySelector('.section-content-tabs');
    if (!el) return;
    const makeTab = (title, url, active) => `<li><a class="content-tab${active && ' active'}" href="${url}" title="${title}">${title}</a></li>`
    const tabSourceEls = Array.from(document.querySelectorAll('[data-content-tab-id]'));
    tabSourceEls.forEach(el => el.setAttribute('id', el.getAttribute('data-content-tab-id')))
    const tabs = tabSourceEls.map(el => makeTab(
      el.getAttribute('data-content-tab-title'),
      '#' + el.getAttribute('data-content-tab-id'),
    ));
    const pageTitle = this.getPageTitle();
    const hasHashOnLoad = !!window.location.hash;
    tabs.unshift(makeTab(pageTitle, '#', !hasHashOnLoad));
    el.querySelector('ul').innerHTML = tabs.join('');
    const anchors = Array.from(el.querySelectorAll('a'));
    const setActive = e => {
      anchors.forEach(a => a.classList.remove('active'));
      const { target } = e;
      target.classList.add('active');
    }
    anchors.forEach(a => a.addEventListener('click', setActive));
  },
  
  initStackSlides() {
    const stack = document.querySelector('.section-stack-container');
    if (!stack) return;
    const allCards = Array.from(document.querySelectorAll('.section-stack-card'));
    let currentSlideId = 0;
    const goToSlide = n => {
      currentSlideId = n;
      allCards.forEach(c => c.classList.remove('active'));
      allCards[n].classList.add('active');
    }
    const onNext = () => {
      if (currentSlideId < 2) currentSlideId++;
      else currentSlideId = 0;
      goToSlide(currentSlideId);
    }
    const onPrev = () => {
      if (currentSlideId > 0) currentSlideId--;
      else currentSlideId = 2;
      goToSlide(currentSlideId);
    }
    document.querySelector('.section-stack-nav__next-button').addEventListener('click', onNext);
    document.querySelector('.section-stack-nav__prev-button').addEventListener('click', onPrev);
    allCards.forEach((c, i) => c.querySelector('.section-stack-card-inner').addEventListener('click', () => {
      if (i === currentSlideId) return;
      goToSlide(i);
    }))
  },
  detectRatio(el) {
    if (!el) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    const ratio = w / h;
    const orientation = ratio >= 1 ? 'landscape' : 'portrait';
    el.setAttribute('data-element-width', w + 'px');
    el.setAttribute('data-element-height', h + 'px');
    el.setAttribute('data-element-ratio', ratio);
    el.setAttribute('data-element-orientation', orientation);
    setCSSCustomProperty('--element-width', w + 'px', el);
    setCSSCustomProperty('--element-height', h + 'px', el);
    setCSSCustomProperty('--element-ratio', ratio, el);
    setCSSCustomProperty('--element-orientation', orientation, el);
  },
  initLazyVideos() {

    var lazyVideos = [].slice.call(document.querySelectorAll("video.lazy"));

    if ("IntersectionObserver" in window) {
      var lazyVideoObserver = new IntersectionObserver(function (entries, observer) {
        entries.forEach(video => {
          if (video.isIntersecting) {
            for (var source in video.target.children) {
              var videoSource = video.target.children[source];
              if (typeof videoSource.tagName === "string" && videoSource.tagName === "SOURCE") {
                videoSource.src = videoSource.dataset.src;
              }
            }

            video.target.load();
            video.target.classList.remove("lazy");
            lazyVideoObserver.unobserve(video.target);
          }
        });
      });

      lazyVideos.forEach(function (lazyVideo) {
        lazyVideoObserver.observe(lazyVideo);
      });
    }

    this.initRatioWatcher();
    this.initVideoCoverer();

  },
  initRatioWatcher() {
    const els = document.querySelectorAll('[data-watch-ratio]');
    Array.from(els).forEach(el => {
      const handler = () => this.detectRatio(el);
      window.addEventListener('load', handler);
      window.addEventListener('resize', handler);
    });
  },
  initVideoCoverer() {
    const handler = () => {
      const videoSlides = document.querySelectorAll('.section-hero-banner');
      Array.from(videoSlides).forEach(slide => {
        const video = slide.querySelector('video');
        const checkVideoRatio = () => {
          const videoWidth = video.videoWidth;
          const videoHeight = video.videoHeight;
          const videoRatio = videoWidth / videoHeight;
          const containerWidth = slide.clientWidth;
          const containerHeight = slide.clientHeight;
          const containerRatio = containerWidth / containerHeight;
          if (videoRatio >= containerRatio) {
            // video is wider or square; 100% height and auto width.
            video.setAttribute('data-fill-mode', 'fill-height');
          } else {
            video.setAttribute('data-fill-mode', 'fill-width');
          }
        };
        checkVideoRatio();
      })
    }
    window.addEventListener('load', handler);
    window.addEventListener('resize', handler);
  },
  initHeroSlider() {
    const slides = document.querySelectorAll('.site-hero__swiper-container .swiper-slide');
    if (slides.length === 1) return;
    new Swiper('.site-hero__swiper-container', {
      loop: true,
      slidesPerView: 1,
      spaceBetween: 0,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      pagination: {
        el: '.swiper-pagination'
      }
    });
  },

  initFooterSlider() {
    const slides = document.querySelector('.footer-swiper');
    if (slides.length === 1) return;
    new Swiper('.footer-swiper', {
      loop: true,
      autoplay: {
        delay: 3000,
      }
    });
  },

  initToggleQuestion() {
    $('.togglefaq').click(function(e) {
      e.preventDefault();
      var notthis = $('.active').not(this);
      notthis.find('.icon-minus').addClass('icon-plus').removeClass('icon-minus');
      notthis.toggleClass('active').next('.faqanswer').slideToggle(300);
       $(this).toggleClass('active').next().slideToggle("fast");
      $(this).find('i').toggleClass('icon-plus icon-minus');
      if ($(this).hasClass('active')){
        $(this).find('i').find('use').attr('xlink:href','#icon-cross');
      }
      else {
        $(this).find('i').find('use').attr('xlink:href','#icon-arrow-botton');
      }
      });
  },

  highlightCurrentPageLinks() {
    const { origin, pathname } = window.location;
    const originRegExp = new RegExp('^' + origin);
    const anchors = document.querySelectorAll('a');
    const pathnameRegExp = new RegExp('^' + pathname);
    Array.from(anchors).forEach(a => {
      if (a.href) {
        const i = a.href.replace(originRegExp, '');
        const matches = a.classList.contains('fuzzy-match') ? i.match(pathnameRegExp) : i === pathname;
        // console.log(i, pathname, matches);
        if (matches) a.classList.add('active');
      }
    })
  },

}